import { useCallback, useEffect, useState } from "react";
import { CornerDownRight, Globe, Loader2, Megaphone, Pin, PinOff, Reply, Search, UserSquare2 } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { AnnouncementsNotice, DeptGroup, officeLabel } from "./DeptGroup";
import { toast } from "sonner";
import { api, errText } from "../../connect/api";
import { useConnect } from "../../connect/store";
import { Avatar, CEO_TITLE, dayLabel, Empty, Header, Spinner } from "./ui";
import { SeenByButton, useFeedSeenTracker } from "./SeenBy";
import { BroadcastPost } from "./BroadcastPost";

const ReplyBox = ({ post, onDone }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/group/posts/${post.id}/reply`, { text: text.trim() });
      toast.success("Reply posted to All Staff India");
      onDone(data);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 flex gap-2 nxc-fade-in">
      <input className="nxc-input py-2 text-sm" placeholder='e.g. "HR please look into this"' value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} autoFocus data-testid="group-reply-input" />
      <button onClick={send} disabled={busy || !text.trim()} className="nxc-btn nxc-btn-gold h-10 shrink-0" data-testid="group-reply-send">{busy ? <Loader2 size={14} className="animate-spin" /> : "Post"}</button>
    </div>
  );
};

const PinnedNote = ({ pinned, isCeo, onChange }) => {
  const [edit, setEdit] = useState(false);
  const [text, setText] = useState(pinned?.text || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setText(pinned?.text || ""); }, [pinned?.text]);
  const save = async (t) => {
    setBusy(true);
    try { const { data } = await api.put("/admin/pinned", { text: t }); onChange(data.text ? data : null); setEdit(false); toast.success(t ? "Pinned to the top for all staff" : "Pinned note removed"); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  if (!pinned && !isCeo) return null;
  if (!pinned && !edit) return <button onClick={() => setEdit(true)} className="w-full nxc-card p-3 text-xs text-slate-400 flex items-center gap-2 hover:bg-white/[0.04]" data-testid="pin-add-button"><Pin size={14} className="text-amber-300" /> Pin a note that every staff member sees first</button>;
  return (
    <div className="nxc-card p-3.5 !border-amber-500/50 !bg-amber-950/25 nxc-fade-in" data-testid="pinned-note">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-amber-300"><Pin size={13} /> Pinned by the Group CEO{pinned?.at ? <span className="text-amber-300/60 normal-case tracking-normal">· {dayLabel(pinned.at)}</span> : null}
        {isCeo && !edit && <span className="ml-auto flex gap-1"><button onClick={() => setEdit(true)} className="nxc-btn nxc-btn-ghost h-7 px-2 text-[11px]" data-testid="pin-edit-button">Edit</button><button onClick={() => save("")} disabled={busy} className="nxc-btn nxc-btn-ghost h-7 px-2 text-[11px]" data-testid="pin-remove-button"><PinOff size={12} /> Unpin</button></span>}
      </div>
      {edit ? (
        <div className="mt-2 space-y-2">
          <textarea className="nxc-input min-h-[90px] text-sm" value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Office closed on Friday for Onam. Wishing all staff a joyful festival. — Management" maxLength={1500} autoFocus data-testid="pin-textarea" />
          <div className="flex gap-2 justify-end"><button onClick={() => { setEdit(false); setText(pinned?.text || ""); }} className="nxc-btn nxc-btn-ghost h-9 text-xs">Cancel</button><button onClick={() => save(text.trim())} disabled={busy || !text.trim()} className="nxc-btn nxc-btn-gold h-9 text-xs" data-testid="pin-save-button">{busy ? <Loader2 size={13} className="animate-spin" /> : <Pin size={13} />} Pin for everyone</button></div>
        </div>
      ) : <p className="text-sm text-slate-100 mt-2 whitespace-pre-wrap leading-relaxed" data-testid="pinned-text">{pinned.text}</p>}
    </div>
  );
};

export const GroupFeed = () => {
  const { me } = useConnect();
  const [params, setParams] = useSearchParams();
  const [groups, setGroups] = useState(null);
  useEffect(() => { api.get("/groups").then((r) => setGroups(r.data)).catch(() => setGroups({ departments: [], offices: [] })); }, []);
  const hasOffice = !groups || groups.offices.length > 0;
  const allStaff = me.is_ceo || (me.perms || {}).group_visibility !== "own_dept";
  const tab = params.get("tab") === "dept" || !allStaff ? "dept" : params.get("tab") === "office" && hasOffice ? "office" : "all";
  const [posts, setPosts] = useState([]);
  const [members, setMembers] = useState(0);
  const [realMembers, setRealMembers] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [replyTo, setReplyTo] = useState(null);
  const [pinned, setPinned] = useState(null);
  const observe = useFeedSeenTracker(!me.is_ceo && tab === "all");

  const load = useCallback(async (before) => {
    if (!allStaff) { setLoading(false); return; }
    const { data } = await api.get("/group/posts", { params: { before: before || "", limit: 30 } });
    setMembers(data.members);
    if (data.real_members != null) setRealMembers(data.real_members);
    if (!before) setPinned(data.pinned && data.pinned.text ? data.pinned : null);
    setHasMore(data.has_more);
    setPosts((prev) => (before ? [...prev, ...data.items] : data.items));
    setLoading(false);
  }, [allStaff]);

  useEffect(() => {
    load().then(() => api.post("/group/opened", { post_ids: [] }).catch(() => {})).catch((e) => toast.error(errText(e)));
    const t = setInterval(() => load().catch(() => {}), 15000);
    return () => clearInterval(t);
  }, [load]);

  const onScroll = (e) => {
    const el = e.currentTarget;
    if (hasMore && !loading && el.scrollTop + el.clientHeight >= el.scrollHeight - 300) { setLoading(true); load(posts[posts.length - 1]?.posted_at).catch(() => setLoading(false)); }
  };

  return (
    <div className="h-full flex flex-col">
      <Header title={tab === "dept" ? (me.is_ceo ? "Department groups" : `${me.department}`) : tab === "office" ? (me.is_ceo ? "Office groups" : officeLabel(me.office)) : "All Staff · India"} sub={tab === "dept" ? (me.is_ceo ? "all 14 departments · everyone can post" : "department group · everyone can post") : tab === "office" ? (me.is_ceo ? "11 international offices · everyone can post" : "office group · everyone can post") : `${members.toLocaleString()} members · corporate announcements`} right={<>{me.is_ceo && tab === "all" && <Link to="/connect/admin/send-as?all=1" className="nxc-btn nxc-btn-gold h-8 !px-2.5 text-[11px]" data-testid="group-send-as-button"><UserSquare2 size={13} /> Send as…</Link>}<Link to="/connect/search" className="p-2 rounded-full hover:bg-white/10" data-testid="group-search-button" aria-label="Search"><Search size={20} /></Link><span className="w-9 h-9 rounded-full bg-blue-500/15 border border-blue-500/30 flex items-center justify-center"><Globe size={16} className="text-blue-400" /></span></>} />
      <div className="flex border-b border-white/10 bg-[#0e1626]" data-testid="group-tabs">
        {[...(allStaff ? [["all", "All Staff India"]] : []), ["dept", me.is_ceo ? "Departments" : (me.perms || {}).dept_visibility === "all" ? "Departments" : "My department"], ...(hasOffice && allStaff ? [["office", me.is_ceo ? "Offices" : "My office"]] : [])].map(([k, l]) => (
          <button key={k} onClick={() => setParams(k === "all" ? {} : { tab: k })} className={`flex-1 py-2.5 text-xs font-semibold border-b-2 transition-colors ${tab === k ? "border-blue-400 text-blue-300" : "border-transparent text-slate-400"}`} data-testid={`group-tab-${k}`}>{l}</button>
        ))}
      </div>
      {tab !== "all" && !groups && <div className="flex justify-center py-12"><Spinner /></div>}
      {tab !== "all" && groups && <DeptGroup key={tab} kind={tab} groups={groups} />}
      {tab === "all" && (<>
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5" onScroll={onScroll} data-testid="group-feed">
        {!(loading && posts.length === 0) && <PinnedNote pinned={pinned} isCeo={me.is_ceo} onChange={setPinned} />}
        {loading && posts.length === 0 && <div className="flex justify-center py-12"><Spinner /></div>}
        {!loading && posts.length === 0 && <Empty icon={Megaphone} title="No announcements yet" text="Corporate updates from all departments appear here during working hours." />}
        {posts.map((p) => (
          <article key={p.id} ref={observe} data-post-id={p.id} className={`nxc-card p-3.5 nxc-fade-in ${p.is_ceo ? "!border-amber-500/40 !bg-amber-950/20" : ""}`} data-testid={`group-post-${p.id}`}>
            <div className="flex items-center gap-2.5">
              <Avatar name={p.sender.name} src={p.sender.avatar} size={38} ceo={p.is_ceo} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-100 truncate">{p.sender.name}</span>
                  {p.is_ceo && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 shrink-0">CEO</span>}
                </div>
                <div className="text-[11px] text-slate-400 truncate">{p.is_ceo ? CEO_TITLE : `${p.sender.title || ""} · ${p.sender.department}`}</div>
              </div>
              <div className="text-[11px] text-slate-500 shrink-0">{dayLabel(p.posted_at)}</div>
            </div>
            {p.parent && (
              <div className="mt-2.5 ml-1 pl-3 border-l-2 border-amber-500/50 text-xs text-slate-400 flex gap-1.5">
                <CornerDownRight size={13} className="shrink-0 mt-0.5 text-amber-500/70" />
                <span><span className="text-slate-300 font-medium">{p.parent.sender_name}</span> ({p.parent.department}): {p.parent.text}</span>
              </div>
            )}
            {p.broadcast_id ? <div className="mt-2.5"><BroadcastPost p={p} me={me} compact /></div> : <p className="mt-2.5 text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">{p.text}</p>}
            {me.is_ceo && (p.is_ceo || p.source === "custom") && <div className="mt-2"><SeenByButton postId={p.id} count={p.seen} total={realMembers} /></div>}
            {me.is_ceo && !p.is_ceo && (
              <div className="mt-2.5">
                {replyTo === p.id ? <ReplyBox post={p} onDone={(r) => { setReplyTo(null); setPosts((prev) => [r, ...prev]); }} /> : (
                  <button onClick={() => setReplyTo(p.id)} className="text-xs text-amber-300/90 hover:text-amber-200 inline-flex items-center gap-1 font-medium" data-testid={`group-reply-button-${p.id}`}><Reply size={13} /> Reply as CEO</button>
                )}
              </div>
            )}
          </article>
        ))}
        {loading && posts.length > 0 && <div className="flex justify-center py-3"><Spinner /></div>}
      </div>
      {!me.is_ceo && <AnnouncementsNotice onGoDept={() => setParams({ tab: "dept" })} />}
      </>)}
    </div>
  );
};
